import React, { useState } from 'react'
import { Spinner } from '../../components/Common/Loader'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { useNavigate } from 'react-router-dom'
import { Eye, EyeOff } from 'lucide-react'
import loginImg from '../../assets/images/login.png'
import { loginUser } from '../../api/login'
import { useAppDispatch } from '../../store/hooks'
import { fetchProfile, setProfile } from '../../store/authSlice'
import { fetchDistributorChannels } from '../../store/distributorChannelSlice'

// Validation schema
const loginSchema = z.object({
  username: z.string().min(1, { message: 'Username is required' }),
  password: z.string().min(1, { message: 'Password is required' }),
})

type LoginFormInputs = z.infer<typeof loginSchema>

const Login: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const [isLoading, setIsLoading] = useState(false)
  const [loginError, setLoginError] = useState<string | null>(null)
  const [showPassword, setShowPassword] = useState(false)

  React.useEffect(() => {
    const token = localStorage.getItem('gowardhan_erp_token')
    const session = localStorage.getItem('gowardhan_erp_session')
    if (token || session === 'true') {
      navigate('/dashboard', { replace: true })
    }
  }, [navigate])

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  })

  const onSubmit = async (data: LoginFormInputs) => {
    setIsLoading(true)
    setLoginError(null)

    try {
      const response = await loginUser({
        user_id: data.username,
        password: data.password,
      })

      localStorage.setItem('gowardhan_erp_session', 'true')
      if (response?.data?.token) {
        localStorage.setItem('gowardhan_erp_token', response.data.token)
      }
      // User data Redux store mein save karo — login response se
      if (response?.data?.user) {
        dispatch(setProfile(response.data.user))
      }
      // Fresh profile data & distributor channels fetch karo API se
      dispatch(fetchProfile())
      dispatch(fetchDistributorChannels())
      
      setIsLoading(false)
      navigate('/dashboard', { replace: true })
    } catch (error: any) {
      setLoginError(error.message || 'Authentication failed. Please verify your credentials.')
      setIsLoading(false)
    }
  }


  return (
    <div className="w-full min-h-screen md:h-screen flex flex-col md:flex-row bg-[#FFF8E1] overflow-y-auto md:overflow-hidden transition-colors duration-300">
      
      {/* Left Panel: Form Section */}
      <div className="w-full md:w-1/2 min-h-screen md:min-h-0 md:h-full flex flex-col justify-center px-8 py-12 sm:px-16 md:px-20 lg:px-28 overflow-y-auto">
        <div className="max-w-md w-full mx-auto">
          
          {/* Header */}
          <div className="mb-10">
            <h1 className="text-3xl font-extrabold text-[#C62828] leading-tight m-0">
              Welcome to our CRM.
            </h1>
            <h2 className="text-3xl font-extrabold text-[#C62828] leading-tight m-0 mt-1">
              Sign In to getting started.
            </h2>
            <p className="text-sm text-[#5D4037] font-medium m-0 mt-3">
              Always verify the URL before entering the credentials!
            </p>
          </div>

          {/* Form Error feedback */}
          {loginError && (
            <div className="mb-6 p-4 rounded-lg bg-rose-50 border border-rose-100 flex items-start gap-2.5">
              <svg className="w-4 h-4 go_erp-text shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-xs font-semibold text-rose-650">{loginError}</span>
            </div>
          )}

          {/* Input Fields */}
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            
            {/* Username Field */}
            <div>
              <label htmlFor="username" className="block text-xs font-semibold text-slate-700 mb-1.5 tracking-wider">
                Username
              </label>
              <div className="relative">
                <input
                  id="username"
                  type="text"
                  {...register('username')}
                  placeholder="Enter Username"
                  className={`w-full pr-12 py-3 border-b border-[#ECECF2] ${
                    errors.username ? 'border-rose-450 focus:border-rose-500' : 'border-slate-200 focus:border-slate-400'
                  } outline-none text-slate-800 text-sm transition-all duration-150`}
                />
                {/* User Outline Icon */}
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none">
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                  </svg>
                </span>
              </div>
              {errors.username && (
                <p className="mt-1 text-xs text-rose-505 font-semibold flex items-center gap-1">
                  {errors.username.message}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-xs font-semibold text-slate-700 mb-1.5 tracking-wider">
                Password
              </label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password')}
                  placeholder="Start typing..."
                  className={`w-full pr-12 py-3 border-b border-[#ECECF2] ${
                    errors.password ? 'border-rose-450 focus:border-rose-500' : 'border-slate-200 focus:border-slate-400'
                  } outline-none text-slate-800 text-sm transition-all duration-150`}
                />
                {/* Toggle Show/Hide Password */}
                <button
                  type="button"
                  onClick={() => setShowPassword((prev) => !prev)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none transition-colors"
                  title={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-rose-505 font-semibold flex items-center gap-1">
                  {errors.password.message}
                </p>
              )}
            </div>

            {/* Sign In Button */}
            <button
              type="submit"
              disabled={isLoading}
              className={`w-full py-3.5 go_erp-bg active:bg-[#8F1A1E] text-white font-bold rounded-lg transition-all duration-150 text-sm mt-8 hover:shadow-none flex items-center justify-center gap-2`}
            >
              {isLoading ? (
                <>
                  <Spinner size="sm" color="text-white" />
                  <span>Verifying...</span>
                </>
              ) : (
                <span>Sign In</span>
              )}
            </button>

          </form>

        </div>
      </div>

      {/* Right Panel: Product branding Showcase */}
      <div className="hidden md:block md:w-1/2 h-full relative bg-[#F9C327] overflow-hidden">
        <img
          src={loginImg}
          alt="Gowardhan Product Showcase"
          className="w-full h-full object-cover block select-none"
          draggable="false"
        />
      </div>

    </div>
  )
}

export default Login
